import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useToast } from "@/plugins/toast/composables/toast-instance";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import {
  BuddyListModel,
  BuddyRequestModel,
  ClassListingStudentModel,
} from "@modules/study-buddy/types.ts";
import StudyBuddyApi from "@apis/study.buddy.api.ts";
import { ApiResponse } from "@apis/api.ts";
import StudentGroupApi, { ActiveStudentGroupResponse } from "@apis/student.group.api";
import { isNull } from "@utils/var.check";
import { FROM_LOCATION } from "@modules/study-buddy/service/study.buddy.constant";
import { StudentGroupActiveGroup } from "@/modules/student-group/student-group-types";

type FromLocation = (typeof FROM_LOCATION)[keyof typeof FROM_LOCATION];

export const useStudyBuddy = (fromLocation: FromLocation) => {
  const store = useStore();
  const { t } = useI18n();
  const toast = useToast();

  const studyBuddyIds = computed(() => store.getters[`${NAMESPACE.ATTEMPT}/getStudyBuddyIds`]);
  const studentList = computed(
    () => store.getters[`${NAMESPACE.ATTEMPT}/getStudentListSortByAlphabetical`],
  );

  const isFromLocationBuddyList = computed(() => fromLocation === FROM_LOCATION.BUDDY_LIST);
  const isFromLocationAssignment = computed(() => fromLocation === FROM_LOCATION.ASSIGNMENT);

  const setStudyBuddyIds = (ids) => store.dispatch(`${NAMESPACE.ATTEMPT}/setStudyBuddyIds`, ids);

  const setStudyBuddyIdsIntoAttemptStore = (studyBuddies: { userId }[]): void => {
    const studyBuddyIdsArray = studyBuddies.map((s) => s.userId);
    const studentListIdsSet = new Set(studentList.value.map((s) => s.userId));
    const studyBuddyIdsInAssignment = studyBuddyIdsArray.filter((id) => studentListIdsSet.has(id));
    setStudyBuddyIds(studyBuddyIdsInAssignment);
  };

  const isMyStudyBuddy = (userId): boolean => studyBuddyIds.value.includes(userId);

  const fetchStudyBuddyListing = async (): Promise<BuddyListModel[]> => {
    const response: ApiResponse<BuddyListModel[]> = await StudyBuddyApi.fetchStudyBuddyListing();

    if (response.code !== "ok") {
      throw new Error();
    }

    return response.data as BuddyListModel[];
  };

  const fetchBuddyRequests = async (): Promise<BuddyListModel[]> => {
    const response: ApiResponse<BuddyListModel[]> =
      await StudyBuddyApi.fetchPendingAcceptanceBuddyRequests();

    if (response.code !== "ok") {
      throw new Error();
    }

    return response.data as BuddyListModel[];
  };

  const fetchAllPendingBuddyRequests = async (): Promise<BuddyRequestModel[]> => {
    const response: ApiResponse<BuddyRequestModel[]> =
      await StudyBuddyApi.fetchAllPendingBuddyRequests();

    if (response.code !== "ok") {
      throw new Error();
    }

    return response.data as BuddyRequestModel[];
  };

  const fetchMyActiveStudentGroups = async (params): Promise<StudentGroupActiveGroup[]> => {
    const response: ApiResponse<ActiveStudentGroupResponse> =
      await StudentGroupApi.fetchMyActiveStudentGroups(params);

    if (response.code !== "ok") {
      throw new Error();
    }

    return response.data.studentGroup as StudentGroupActiveGroup[];
  };

  const fetchStudentGroupStudent = async (params): Promise<ClassListingStudentModel[]> => {
    const response = await StudentGroupApi.fetchStudentGroupStudent(params);

    if (!isNull(response.error)) {
      throw response.error;
    }

    return response.data;
  };

  const showMaximumBuddyRequestError = () => {
    toast.show("error", {
      title: t("study_buddy_maximum_buddy_request_error_title"),
      subTitle: t("study_buddy_maximum_buddy_request_error_subtitle"),
      hideCloseButton: false,
      hasTimeout: false,
    });
  };

  return {
    isFromLocationBuddyList,
    isFromLocationAssignment,
    setStudyBuddyIdsIntoAttemptStore,
    isMyStudyBuddy,
    fetchStudyBuddyListing,
    fetchBuddyRequests,
    fetchAllPendingBuddyRequests,
    fetchMyActiveStudentGroups,
    fetchStudentGroupStudent,
    showMaximumBuddyRequestError,
  };
};
